import React, { useEffect, useMemo } from 'react';

import {
  CreateEntityInput,
  EntityUserRole,
  User,
  selectedEntityIdInVar,
  EntityUserStatus,
  EntityUsersByUserQuery,
} from '@admiin-com/ds-graphql';
import { CSGetSub as GET_SUB, OnboardingStatus } from '@admiin-com/ds-graphql';
import {
  updateUser as UPDATE_USER,
  getUser as GET_USER,
  createEntity as CREATE_ENTITY,
  entityUsersByUser as ENTITY_USERS_BY_USER,
  CSGetEntity as GET_ENTITY,
} from '@admiin-com/ds-graphql';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { redirectToOnboarding } from '../../helpers/onboarding';
import {
  getNextOnboardingStep,
  getPrevOnboardingStep,
  needsOnboarding,
} from '@admiin-com/ds-common';
import { PATHS } from '../../navigation/paths';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { useUserEntities } from '../../hooks/useUserEntities/useUserEntities';

export enum OnboardingBusinessStep {
  ACCOUTANT_FIRM = 'accointant-firm',
  ADD_BUSINESS = 'add-business',
  BUSINESS_ADDRESS = 'business-address',
  BUSINESS_LOGO = 'business-logo',
  BUSINESS_BRAND = 'business-brand',
  BUSINESS_VERIFY = 'business-verify',
}
export const onBoardingBusinessSteps = Object.values(OnboardingBusinessStep);

export const OnboardingProcessContext = React.createContext<any>(null);

interface BusinessProcessProviderProps {
  children: React.ReactNode;
  isDialog?: boolean;
  onFinal?: () => void;
}
export const BusinessProcessProvider = ({
  isDialog = false,
  children,
  onFinal,
}: BusinessProcessProviderProps) => {
  const isNotOnboarding = isDialog;
  const initalBusinessStep = !isNotOnboarding
    ? OnboardingBusinessStep.ACCOUTANT_FIRM
    : OnboardingBusinessStep.ADD_BUSINESS;
  const { data: subData, loading: loadingSub } = useQuery(gql(GET_SUB));
  const { data: userData, loading: loadingUser } = useQuery(gql(GET_USER), {
    variables: {
      id: subData?.sub,
    },
    skip: !subData || !subData?.sub,
  });
  const sub = subData?.sub;
  const user: User = useMemo(() => userData?.getUser || {}, [userData]);

  const currentOnboardingStatus = user.onboardingStatus;
  const { data: entityData, loading: loadingEntity } = useQuery(
    gql(GET_ENTITY),
    {
      variables: {
        id: user.onboardingEntity,
      },
      skip:
        !user.onboardingEntity ||
        currentOnboardingStatus !== OnboardingStatus.BUSINESS,
    }
  );

  const [updateUser, { error: updateUserError, loading: updatingUser }] =
    useMutation(gql(UPDATE_USER));

  const [createEntity, { error: createEntityError }] = useMutation(
    gql(CREATE_ENTITY),
    {
      update: (cache, { data: { createEntity } }) => {
        try {
          // Read current cache data with proper fallback for empty results
          const existingData = cache.readQuery<EntityUsersByUserQuery>({
            query: gql(ENTITY_USERS_BY_USER),
            variables: { userId: sub },
          });

          // Create complete entity user entry with all required fields
          const newEntityUser = {
            __typename: 'EntityUser',
            id: `${createEntity.id}-${sub}`,
            entityId: createEntity.id,
            userId: sub,
            firmId: null,
            email: null,
            invitedEmail: user?.email || null,
            referredBy: null,
            invitedEntityId: null,
            firmEntityId: null,
            firstName: createEntity.firstName || '',
            lastName: createEntity.lastName || '',
            entity: createEntity,
            user: { __typename: 'User', id: sub },
            role: EntityUserRole.OWNER,
            paymentsEnabled: true,
            termsInvoiceAccepted: null,
            termsOcrAccepted: null,
            entitySearchName: createEntity.name?.toLowerCase() || '',
            searchName: `${createEntity.firstName || ''} ${
              createEntity.lastName || ''
            }`.toLowerCase(),
            taxNumber: createEntity.taxNumber || null,
            companyNumber: createEntity.companyNumber || null,
            createdBy: sub,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            status: EntityUserStatus.ACCEPTED,
          };

          // Write updated data to cache
          cache.writeQuery({
            query: gql(ENTITY_USERS_BY_USER),
            variables: { userId: sub },
            data: {
              entityUsersByUser: {
                __typename: 'EntityUserConnection',
                items: [
                  ...(existingData?.entityUsersByUser?.items || []),
                  newEntityUser,
                ],
                total: (existingData?.entityUsersByUser?.total || 0) + 1,
              },
            },
          });
        } catch (error) {
          console.error('Error updating cache:', error);
        }
      },
    }
  );

  const navigate = useNavigate();

  const [currentBusinessStepIndex, setCurrentBusinessStepIndex] =
    React.useState(!isNotOnboarding ? 0 : 1);

  const methods = useForm<{ entity: CreateEntityInput }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { setValue, watch } = methods;

  useEffect(() => {
    if (entityData) {
      setValue('entity', entityData);
    }
  }, [entityData, setValue]);
  const currentBusinessStep = useMemo(
    () => onBoardingBusinessSteps[currentBusinessStepIndex],
    [currentBusinessStepIndex]
  );
  const getNextStep = () =>
    onBoardingBusinessSteps[currentBusinessStepIndex + 1];
  const previous = () =>
    setCurrentBusinessStepIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  const nextBusiness = () => {
    setCurrentBusinessStepIndex((prevIndex) =>
      Math.min(prevIndex + 1, onBoardingBusinessSteps.length - 1)
    );
  };
  const { refetch: refetchEntities } = useUserEntities({
    role: EntityUserRole.OWNER,
  });
  const createEntityFromOnboarding = async (input: any) => {
    const updatedData = await createEntity({
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: gql(GET_USER),
          variables: {
            id: sub,
          },
        },
      ],

      awaitRefetchQueries: true,
    });
    await refetchEntities();
    const newEntityId = updatedData?.data.createEntity?.id;
    await updateUser({
      variables: {
        input: {
          id: sub,
          selectedEntityId: newEntityId,
          ...(!isNotOnboarding && { onboardingEntity: newEntityId }),
        },
      },
    });
    selectedEntityIdInVar(newEntityId);
    localStorage.setItem('selectedEntityId', newEntityId);
  };
  const showSnackbar = useSnackbar();
  const { t } = useTranslation();
  const gotoOnboarding = async (onboardingStatus: OnboardingStatus) => {
    if (isNotOnboarding) {
      if (onboardingStatus === OnboardingStatus.COMPLETED) {
        onFinal && onFinal();
        try {
          const entity = watch('entity');
          // if (entity.type === EntityType.INDIVIDUAL) {
          //   entity.name = `${entity.firstName} ${entity.lastName}`;
          // }
          await createEntityFromOnboarding(entity);
          showSnackbar({
            message: t('entityCreated', { ns: 'onboarding' }),
            severity: 'success',
            horizontal: 'right',
            vertical: 'bottom',
          });
        } catch (err: any) {
          showSnackbar({
            message: err.message,
            severity: 'error',
            vertical: 'bottom',
            horizontal: 'right',
          });
        }
      }
      return;
    }
    try {
      if (needsOnboarding(onboardingStatus)) {
        const updatedData = await updateUser({
          variables: {
            input: {
              onboardingStatus: onboardingStatus,
              id: subData?.sub,
            },
          },
        });

        const updatedUser = updatedData?.data?.updateUser;
        redirectToOnboarding(updatedUser, navigate);
      } else {
        await updateUser({
          variables: {
            input: {
              onboardingStatus: onboardingStatus,
              id: subData?.sub,
            },
          },
        });
        navigate(PATHS.onboardingComplete);
      }
      initBusinessStep();
    } catch (error) {
      throw new Error("can't go to next onboarding step");
    }
  };

  const nextOnboarding = async () => {
    await gotoOnboarding(getNextOnboardingStep(user));
  };
  const prevOnboarding = async () => {
    await gotoOnboarding(getPrevOnboardingStep(user));
  };

  const finishOnboarding = async () => {
    await gotoOnboarding(OnboardingStatus.COMPLETED);
  };

  const initBusinessStep = () => {
    setCurrentBusinessStepIndex(0);
  };

  const gotoPrev = () => {
    if (isNotOnboarding) {
      if (currentBusinessStep === OnboardingBusinessStep.ADD_BUSINESS) {
        onFinal && onFinal();
        return;
      }
      previous();
    }
    if (
      currentOnboardingStatus !== OnboardingStatus.BUSINESS ||
      (currentOnboardingStatus === OnboardingStatus.BUSINESS &&
        currentBusinessStep === initalBusinessStep)
    ) {
      prevOnboarding();
    } else {
      previous();
    }
  };

  const loading = useMemo(
    () => loadingUser || loadingEntity || loadingSub,
    [loadingEntity, loadingSub, loadingUser]
  );

  return (
    <OnboardingProcessContext.Provider
      value={{
        isNotOnboarding: isNotOnboarding,
        currentOnboardingStatus,
        currentBusinessStep,
        getNextStep,
        initBusinessStep,
        updatingUser,
        updateUser,
        updateUserError,
        user,
        sub: subData?.sub,
        onboardingEntityId: user.onboardingEntity,
        nextBusiness,
        gotoPrev,
        onFinal,
        nextOnboarding,
        loading,
        createEntityFromOnboarding,
        finishOnboarding,
        createEntityError,
      }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </OnboardingProcessContext.Provider>
  );
};

export const useOnboardingProcess = () => {
  const context = React.useContext(OnboardingProcessContext);
  if (!context) {
    throw new Error(
      'useBusinessProcess must be used within a BusinessProcessProvider'
    );
  }
  return context;
};
